import { PropsWithChildren, useEffect, useState } from "react";
import {
  getImportReportChanges,
  ImportReportChangeModel,
  ImportReportChangeType,
} from "../../clients/BackOfficeApi";
import {
  Box,
  Spinner,
  Table,
  TableCaption,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";

const isAddition = (change: ImportReportChangeModel) =>
  change.changeType === ImportReportChangeType.ADDITION;
const isUpdate = (change: ImportReportChangeModel) =>
  change.changeType === ImportReportChangeType.UPDATE;

interface Props {
  changes: ImportReportChangeModel[];
}

function FilteredChanges({ changes, children }: PropsWithChildren<Props>) {
  const { isOpen, onToggle } = useDisclosure();

  if (changes.length === 0) {
    return null;
  }

  return (
    <Table variant="striped">
      <TableCaption
        _hover={{ bg: "gray.100" }}
        cursor="pointer"
        onClick={onToggle}
        placement="top"
        role="button"
        tabIndex={0}
        transition="300ms"
      >
        {children} ({changes.length})
        {isOpen ? <ChevronDownIcon ml={2} /> : <ChevronRightIcon ml={2} />}
      </TableCaption>
      <Thead hidden={!isOpen}>
        <Tr>
          <Th>Rij</Th>
          <Th>Kolom</Th>
          <Th>Waarde</Th>
        </Tr>
      </Thead>
      <Tbody hidden={!isOpen}>
        {changes.map((c) => (
          <Tr key={c.id}>
            <Td>{c.rowIndex}</Td>
            <Td>{c.columnName}</Td>
            <Td>{c.representation}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

interface ChangesSummaryProps {
  reportId: number;
}
export function ChangesSummary({ reportId }: ChangesSummaryProps) {
  const [changes, setChanges] = useState<ImportReportChangeModel[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();

    setLoading(true);

    getImportReportChanges({
      path: { importReportId: reportId },
      query: { Offset: 0, Limit: 500 },
      throwOnError: true,
      signal: abortController.signal,
    })
      .then(({ data }) => {
        if (abortController.signal.aborted) return;

        if (data) setChanges(data.items);
      })
      .catch((e) => {
        if (abortController.signal.aborted) return;

        console.error(`Error fetching changes for report ${reportId}:`, e);
      })
      .finally(() => setLoading(false));

    return () => abortController.abort("Component unmounted or re-rendered");
  }, [reportId]);

  if (loading) return <Spinner />;

  if (changes.length === 0) {
    return <Box>Geen wijzigingen gevonden</Box>;
  }

  return (
    <Box>
      <FilteredChanges changes={changes.filter(isAddition)}>
        Nieuwe waarden
      </FilteredChanges>
      <FilteredChanges changes={changes.filter(isUpdate)}>
        Aangepaste waarden
      </FilteredChanges>
    </Box>
  );
}
